import { FDRStatus } from '@cibo/core'
import { usePaginatedFields } from '@cibo/landmanager'
import { MapTheme, useFieldSearch } from '@cibo/mapgl'
import { useMapPin } from '@cibo/mapgl/src/hooks'
import { Loading } from '@cibo/profile'
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded'
import { Box, Button, Paper, Stack, Typography, useTheme } from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Layer, Source, useMap } from 'react-map-gl/dist/es5'
import { Link } from 'react-router-dom'
import { ADMIN_ROUTES } from '../../navigation/types'
import { useAppStore } from '../../store'

interface EnrolledFieldsLayerProps {}

export const EnrolledFieldsLayer = (props: EnrolledFieldsLayerProps) => {
  const { t } = useTranslation('EnrolledFieldsLayer')
  const { palette } = useTheme<MapTheme>()
  const { current } = useMap()
  const { bounds, setBounds } = useAppStore()

  useEffect(() => {
    if (!!current) {
      current.on('moveend', e => {
        setBounds(current.getBounds().toArray())
      })
      setBounds(current.getBounds().toArray())
    }
  }, [current])

  //fetch all enrolled fields
  const enrolledFields = usePaginatedFields(0, 1, {
    fdrStatusAllOf: [{ status: FDRStatus.enrolled }],
  })
  const successPin = useMapPin('enrolled', palette.pinSuccessFill, palette.pinSuccessStroke)
  const enrolledGeoJSON = useFieldSearch(
    {
      lowerLeft: bounds?.[0] as [number, number],
      upperRight: bounds?.[1] as [number, number],
      fdrStatusAllOf: [{ status: FDRStatus.enrolled }],
    },
    { enabled: !!bounds }
  )

  return (
    <>
      <Source
        type="geojson"
        data={enrolledGeoJSON.data || { type: 'FeatureCollection', features: [] }}
        id="enrolledGeoJSON"
      >
        <Layer
          type="symbol"
          layout={{
            'icon-image': successPin,
            'icon-size': 0.5,
            'icon-allow-overlap': true,
            'icon-anchor': 'bottom',
          }}
          filter={['==', ['geometry-type'], 'Point']}
          paint={{ 'icon-opacity': ['interpolate', ['linear'], ['zoom'], 0, 1, 10, 1, 10.75, 0] }}
        />
        <Layer
          filter={['!', ['==', ['geometry-type'], 'Point']]}
          type="fill"
          paint={{
            'fill-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0, 10, 0, 10.5, 0.3],
            'fill-color': palette.pinSuccessFill,
          }}
        />
        <Layer
          filter={['!', ['==', ['geometry-type'], 'Point']]}
          type="line"
          paint={{
            'line-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0, 10, 0, 10.5, 1],
            'line-color': palette.pinSuccessFill,
            'line-width': 3,
          }}
        />
      </Source>

      <Box
        padding={1}
        paddingBottom={4}
        position="relative"
        height="100%"
        sx={{ pointerEvents: 'none' }}
      >
        <Stack flex={1}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Paper style={{ pointerEvents: 'all' }}>
              <Button
                component={Link}
                to={ADMIN_ROUTES.PROGRAM_LIST}
                variant="contained"
                startIcon={<ChevronLeftRounded />}
                size="small"
              >
                {t('back')}
              </Button>
            </Paper>
            {enrolledFields.isFetched && (
              <Typography variant="h6">
                {t('enrolledFields', { count: enrolledFields.data?.numAvailable })}
              </Typography>
            )}
          </Stack>
          {enrolledFields.isLoading && <Loading />}
        </Stack>
      </Box>
    </>
  )
}
